import type { Color } from "../../common/color.js";
import { Control, type ControlOptions } from "../core/Control.js";
import type { Rect } from "../../common/geometry.js";
import type { WebwinImage } from "../../engine/ImageStore.js";
import type { RenderContext } from "../render/RenderContext.js";
import type { Theme } from "../themes/Theme.js";

export type ImageSizeMode =
  /** Draw at natural size, anchored top-left, clipped to the control. */
  | "normal"
  /** Fill the control, ignoring aspect ratio. */
  | "stretch"
  /** Scale to fit inside the control, preserving aspect ratio (letterboxed). */
  | "fit"
  /** Scale to cover the control, preserving aspect ratio (cropped). */
  | "fill"
  /** Draw at natural size, centered. */
  | "center";

/**
 * Where a `natural`-sized picture lands inside `bounds` under `mode`.
 *
 * Shared with `VideoControl`, which fits a frame exactly the way this fits a
 * bitmap. Returns null when there is nothing sensible to draw — an empty
 * source or an empty control.
 */
export function fitToSizeMode(
  mode: ImageSizeMode,
  natural: { width: number; height: number },
  bounds: Rect,
): Rect | null {
  const { width: nw, height: nh } = natural;
  if (nw <= 0 || nh <= 0) return null;

  switch (mode) {
    case "stretch":
      return bounds;
    case "normal":
      return { x: 0, y: 0, width: nw, height: nh };
    case "center":
      return {
        x: Math.round((bounds.width - nw) / 2),
        y: Math.round((bounds.height - nh) / 2),
        width: nw,
        height: nh,
      };
    case "fit":
    case "fill": {
      if (bounds.width <= 0 || bounds.height <= 0) return null;
      const sx = bounds.width / nw;
      const sy = bounds.height / nh;
      // `fit` takes the smaller scale, so the whole picture is inside and the
      // spare edge is a letterbox; `fill` takes the larger, so there is no
      // spare edge and the overflow is cropped.
      const scale = mode === "fit" ? Math.min(sx, sy) : Math.max(sx, sy);
      const w = nw * scale;
      const h = nh * scale;
      return { x: (bounds.width - w) / 2, y: (bounds.height - h) / 2, width: w, height: h };
    }
  }
}

export interface ImageControlOptions extends ControlOptions {
  /** A loaded image, or a URL to fetch through the desktop's ImageStore. */
  source?: WebwinImage | string | null;
  sizeMode?: ImageSizeMode;
  /** Multiplies the sampled color; use it to tint or fade. */
  tint?: Color;
  showBorder?: boolean;
  backgroundColor?: Color | null;
  onLoad?: () => void;
  onError?: (error: unknown) => void;
}

/** Displays a bitmap with WinForms-style sizing modes. */
export class ImageControl extends Control {
  #image: WebwinImage | null = null;
  #url: string | null = null;
  #loading = false;
  #error: unknown = null;

  sizeMode: ImageSizeMode;
  tint: Color | null;
  showBorder: boolean;
  backgroundColor: Color | null;

  constructor(options: ImageControlOptions = {}) {
    super({ width: 160, height: 120, ...options });
    this.sizeMode = options.sizeMode ?? "fit";
    this.tint = options.tint ?? null;
    this.showBorder = options.showBorder ?? false;
    this.backgroundColor = options.backgroundColor ?? null;
    if (options.onLoad) this.on("change", options.onLoad);
    this.#onError = options.onError ?? null;
    if (options.source !== undefined) this.source = options.source;
  }

  #onError: ((error: unknown) => void) | null;

  get source(): WebwinImage | string | null {
    return this.#url ?? this.#image;
  }
  set source(v: WebwinImage | string | null) {
    if (typeof v === "string") {
      this.#url = v;
      this.#image = null;
      this.#error = null;
      this.#beginLoad(v);
    } else {
      this.#url = null;
      this.#image = v;
      this.#loading = false;
      this.#error = null;
      this.emit("change");
    }
    this.invalidate();
  }

  get image(): WebwinImage | null {
    return this.#image;
  }

  get loading(): boolean {
    return this.#loading;
  }

  get error(): unknown {
    return this.#error;
  }

  /** Natural size of the loaded image, or null. */
  get naturalSize(): { width: number; height: number } | null {
    return this.#image ? { width: this.#image.width, height: this.#image.height } : null;
  }

  /** Resize the control to the image's natural size. */
  sizeToImage(): void {
    const size = this.naturalSize;
    if (size) this.setSize(size.width, size.height);
  }

  #beginLoad(url: string): void {
    const desktop = this.desktop;
    if (!desktop) return; // retried by onEnvironmentChanged once attached
    this.#loading = true;
    desktop.images
      .load(url)
      .then((image) => {
        // A newer assignment may have replaced this request while it was in flight.
        if (this.#url !== url) return;
        this.#image = image;
        this.#loading = false;
        this.emit("change");
        this.invalidate();
      })
      .catch((err: unknown) => {
        if (this.#url !== url) return;
        this.#loading = false;
        this.#error = err;
        this.#onError?.(err);
        this.invalidate();
      });
  }

  protected override onEnvironmentChanged(): void {
    if (this.#url && !this.#image && !this.#loading) this.#beginLoad(this.#url);
  }

  /** Destination rectangle for the current image and size mode. */
  destRect(): Rect | null {
    const image = this.#image;
    if (!image) return null;
    return fitToSizeMode(this.sizeMode, image, this.localRect);
  }

  protected override onPaint(rc: RenderContext, theme: Theme): void {
    const r = this.localRect;
    if (this.backgroundColor) rc.fillRect(r, this.backgroundColor, theme.metrics.controlCornerRadius);

    const image = this.#image;
    if (image && !image.destroyed) {
      const dest = this.destRect();
      // "normal" and "center" can overflow, and "fill" always crops.
      if (dest) rc.clipped(r, () => rc.drawTexture(image, dest, undefined, this.tint ?? undefined));
    } else {
      const message = this.#error ? "failed to load" : this.#loading ? "loading…" : "";
      if (message) {
        rc.drawTextCentered(message, r, theme.metrics.font, theme.palette.textSubtle);
      }
    }

    if (this.showBorder) {
      rc.strokeRect(r, theme.palette.controlBorder, 1, theme.metrics.controlCornerRadius);
    }
  }
}
